export const ASSET_VIEW_BANDS = {
    BASIC: "Basic Asset Information",
    GEO: "Geo Hierarchy",
    EXTENDED: "Extended Asset Information",
    QA: "Asset QA/QC Status",
    PARENT_CHILD: "Asset Parent/Child Hierarchy",
    ADDITIONAL: "Asset Additional",
    BOILER: "Boiler Fields",
    AUXILIARY: "Auxiliary Equipment Fields"
};

export const ASSET_VIEW_STATIC_BAND_NAMES = new Set(Object.values(ASSET_VIEW_BANDS));

export const DYNAMIC_BAND_KEY_PREFIX = "__dynamic__:";

export const dynamicBandConfigKey = bandName => `${DYNAMIC_BAND_KEY_PREFIX}${bandName}`;

export const isDynamicBandConfigKey = bandKey =>
    typeof bandKey === "string" && bandKey.startsWith(DYNAMIC_BAND_KEY_PREFIX);

export const getDynamicBandDisplayName = bandKey =>
    isDynamicBandConfigKey(bandKey) ? bandKey.slice(DYNAMIC_BAND_KEY_PREFIX.length) : bandKey;

export const getAssetViewFieldBandLabel = (bandKey, fieldConfig) => {
    if (!bandKey) return "Other";
    if (isDynamicBandConfigKey(bandKey)) {
        const display = getDynamicBandDisplayName(bandKey);
        if (ASSET_VIEW_STATIC_BAND_NAMES.has(display)) {
            return `${display} (Dynamic Fields)`;
        }
        return display;
    }
    const sampleKey = (fieldConfig?.keys || []).find(
        k => fieldConfig?.config?.[k] && (fieldConfig.config[k].band === bandKey || fieldConfig.config[k].bandForm === bandKey)
    );
    return fieldConfig?.config?.[sampleKey]?.bandDisplay || bandKey;
};

export const ASSET_VIEW_ALWAYS_VISIBLE_KEYS = new Set(["main_image"]);

const field = (key, label, band, isVisible = true, bandForm = band) => ({
    key,
    label,
    band,
    bandForm,
    isVisible
});

const STATIC_FIELDS = [
    // Basic Asset Information
    field("code", "Asset Code", ASSET_VIEW_BANDS.BASIC),
    field("asset_name", "Asset Name", ASSET_VIEW_BANDS.BASIC),
    field("asset_description", "Asset Description", ASSET_VIEW_BANDS.BASIC),
    field("asset_type_id", "Asset Type", ASSET_VIEW_BANDS.BASIC),
    field("asset_status_id", "Asset Status", ASSET_VIEW_BANDS.BASIC),
    field("client_asset_condition_id", "Asset Condition", ASSET_VIEW_BANDS.BASIC),
    field("asset_condition_description", "Asset Condition Description", ASSET_VIEW_BANDS.BASIC),
    field("asset_ownership", "Asset Ownership", ASSET_VIEW_BANDS.BASIC),
    field("criticality", "Criticality", ASSET_VIEW_BANDS.BASIC),
    field("asset_tag", "Asset Tag", ASSET_VIEW_BANDS.BASIC),
    field("guid", "GUID", ASSET_VIEW_BANDS.BASIC),
    field("model_number", "Model Number", ASSET_VIEW_BANDS.BASIC),
    field("core_model_number", "Core Model Number", ASSET_VIEW_BANDS.BASIC),
    field("serial_number", "Serial Number", ASSET_VIEW_BANDS.BASIC),
    field("capacity", "Capacity", ASSET_VIEW_BANDS.BASIC),
    field("capacity_unit", "Capacity Unit", ASSET_VIEW_BANDS.BASIC),
    field("unit_cost", "Unit Cost", ASSET_VIEW_BANDS.BASIC),
    field("asset_barcode", "Asset Barcode", ASSET_VIEW_BANDS.BASIC),
    field("asset_client_id", "Asset Client ID", ASSET_VIEW_BANDS.BASIC),
    field("asset_cmms_id", "Asset CMMS ID", ASSET_VIEW_BANDS.BASIC),
    field("rtls_tag", "RTLS Tag", ASSET_VIEW_BANDS.BASIC),
    field("main_image", "Main Image", ASSET_VIEW_BANDS.BASIC),

    // Geo Hierarchy
    field("client_id", "Client", ASSET_VIEW_BANDS.GEO),
    field("region_id", "Region", ASSET_VIEW_BANDS.GEO),
    field("site_id", "Site", ASSET_VIEW_BANDS.GEO),
    field("building_id", "Building", ASSET_VIEW_BANDS.GEO),
    field("building_type", "Building Type", ASSET_VIEW_BANDS.GEO),
    field("addition_id", "Addition", ASSET_VIEW_BANDS.GEO),
    field("floor_id", "Floor", ASSET_VIEW_BANDS.GEO),
    field("room_number", "Room Number", ASSET_VIEW_BANDS.GEO),
    field("room_name", "Room Name", ASSET_VIEW_BANDS.GEO),
    field("location", "Location", ASSET_VIEW_BANDS.GEO),
    field("architectural_room_number", "Architectural Room Number", ASSET_VIEW_BANDS.GEO),
    field("additional_room_description", "Additional Room Description", ASSET_VIEW_BANDS.GEO),
    field("latitude", "Latitude", ASSET_VIEW_BANDS.GEO),
    field("longitude", "Longitude", ASSET_VIEW_BANDS.GEO),

    // Extended Asset Information
    field("manufacturer", "Manufacturer", ASSET_VIEW_BANDS.EXTENDED),
    field("year_manufactured", "Year Manufactured", ASSET_VIEW_BANDS.EXTENDED),
    field("installed_year", "Installed Year", ASSET_VIEW_BANDS.EXTENDED),
    field("current_age", "Current Age", ASSET_VIEW_BANDS.EXTENDED),
    field("service_life", "Service Life", ASSET_VIEW_BANDS.EXTENDED),
    field("end_of_service_life", "End of Service Life", ASSET_VIEW_BANDS.EXTENDED),
    field("usefull_life_remaining", "Useful Life Remaining", ASSET_VIEW_BANDS.EXTENDED),
    field("csp_unit", "CSP Unit", ASSET_VIEW_BANDS.EXTENDED),
    field("crv", "CRV", ASSET_VIEW_BANDS.EXTENDED),
    field("fca_cost", "FCA Cost", ASSET_VIEW_BANDS.EXTENDED),
    field("refrigerant", "Refrigerant", ASSET_VIEW_BANDS.EXTENDED),
    field("warranty_start", "Warranty Start", ASSET_VIEW_BANDS.EXTENDED),
    field("warranty_end", "Warranty End", ASSET_VIEW_BANDS.EXTENDED),
    field("install_date", "Install Date", ASSET_VIEW_BANDS.EXTENDED),
    field("startup_date", "Startup Date", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_1_id", "Uniformat Level 1", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_2_id", "Uniformat Level 2", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_3_id", "Uniformat Level 3", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_4_id", "Uniformat Level 4", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_5_id", "Uniformat Level 5", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_6_id", "Uniformat Level 6", ASSET_VIEW_BANDS.EXTENDED),
    field("client_standards_id", "Client Standard", ASSET_VIEW_BANDS.EXTENDED),
    field("client_standards_description", "Client Standard Description", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_6_description", "Uniformat Level 6 Description", ASSET_VIEW_BANDS.EXTENDED),
    field("uniformat_level_3_custom_description", "Uniformat Level 3 Custom Description", ASSET_VIEW_BANDS.EXTENDED),
    field("main_category_id", "Main Category", ASSET_VIEW_BANDS.EXTENDED),
    field("sub_category_1_id", "Sub Category 1", ASSET_VIEW_BANDS.EXTENDED),
    field("sub_category_2_id", "Sub Category 2", ASSET_VIEW_BANDS.EXTENDED),
    field("subcategory2_description", "Sub Category 2 Description", ASSET_VIEW_BANDS.EXTENDED),
    field("sub_category_3_id", "Sub Category 3", ASSET_VIEW_BANDS.EXTENDED),
    field("trade_id", "Trade", ASSET_VIEW_BANDS.EXTENDED),
    field("system_id", "System", ASSET_VIEW_BANDS.EXTENDED),
    field("sub_system_id", "Sub-System", ASSET_VIEW_BANDS.EXTENDED),
    field("asset_note", "Asset Notes", ASSET_VIEW_BANDS.EXTENDED),
    field("energy_use_type", "Energy Use Type", ASSET_VIEW_BANDS.EXTENDED),

    // Asset QA/QC Status
    field("survey_id", "Asset Survey Id", ASSET_VIEW_BANDS.QA),
    field("survey_property_note", "Asset Survey Property Notes", ASSET_VIEW_BANDS.QA),
    field("qa_notes", "Asset Survey QA/QC Notes", ASSET_VIEW_BANDS.QA),
    field("capacity_status", "Capacity Status", ASSET_VIEW_BANDS.QA, true, ASSET_VIEW_BANDS.BASIC),
    field("installed_year_status", "Install Year Status", ASSET_VIEW_BANDS.QA),
    field("name_plate_status", "Name Plate Status", ASSET_VIEW_BANDS.QA),
    field("additional_qa_notes", "Asset Survey Additional QA/QC Notes", ASSET_VIEW_BANDS.QA),
    field("survey_date_created", "Asset Survey Date Created", ASSET_VIEW_BANDS.QA),
    field("surveyor", "Asset Survey Surveyor", ASSET_VIEW_BANDS.QA),
    field("survey_date_edited", "Asset Survey Date Edited", ASSET_VIEW_BANDS.QA),
    field("editor", "Asset Survey Editor", ASSET_VIEW_BANDS.QA),
    field("new_asset", "Is This a New Asset", ASSET_VIEW_BANDS.QA),
    field("source", "Source", ASSET_VIEW_BANDS.QA),
    field("latest_source", "Latest Source", ASSET_VIEW_BANDS.QA),
    field("created_at", "Created At", ASSET_VIEW_BANDS.QA),
    field("updated_at", "Updated At", ASSET_VIEW_BANDS.QA),

    // Asset Parent/Child Hierarchy
    field("area_served", "Area Served", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("upstream_asset_barcode_number", "Upstream Asset Barcode Numbers", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("linked_asset_barcode_number", "Linked Asset Barcode Numbers", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("source_panel_name", "Source Panel Name", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("source_panel_barcode_number", "Source Panel Barcode Number", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("source_panel", "Source Panel Emergency/Normal", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("skysite_hyperlink", "Skysite Hyperlink", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("quantity", "Quantity", ASSET_VIEW_BANDS.PARENT_CHILD),
    field("parent_global_id", "Parent Global ID", ASSET_VIEW_BANDS.PARENT_CHILD),

    // Asset Additional
    field("discharge", "Discharge", ASSET_VIEW_BANDS.ADDITIONAL),
    field("return", "Return", ASSET_VIEW_BANDS.ADDITIONAL),
    field("unit_property_labeled", "Is Unit Properly Labeled", ASSET_VIEW_BANDS.ADDITIONAL),
    field("factory_mounted_disconnect", "Factory Mounted Disconnect", ASSET_VIEW_BANDS.ADDITIONAL),
    field("unit_voltage", "Unit Voltage", ASSET_VIEW_BANDS.ADDITIONAL),
    field("ahu_voltage", "AHU Voltage", ASSET_VIEW_BANDS.ADDITIONAL),
    field("wire_type", "Wire Type", ASSET_VIEW_BANDS.ADDITIONAL),
    field("wire_size", "Wire Size", ASSET_VIEW_BANDS.ADDITIONAL),
    field("estimated_wire_run_length", "Estimated Wire Run Length", ASSET_VIEW_BANDS.ADDITIONAL),
    field("breaker_size", "Breaker Size", ASSET_VIEW_BANDS.ADDITIONAL),
    field("mocp", "MOCP", ASSET_VIEW_BANDS.ADDITIONAL),
    field("mca", "MCA", ASSET_VIEW_BANDS.ADDITIONAL),
    field("liquid_line", "Liquid Line", ASSET_VIEW_BANDS.ADDITIONAL),
    field("suction_line", "Suction Line", ASSET_VIEW_BANDS.ADDITIONAL),
    field("hot_gas_by_pass", "Hot Gas By-Pass", ASSET_VIEW_BANDS.ADDITIONAL),
    field("number_of_circuits", "Number of Circuits", ASSET_VIEW_BANDS.ADDITIONAL),
    field("length_of_line", "Length of Line Run", ASSET_VIEW_BANDS.ADDITIONAL),
    field("damper_type", "Damper Type", ASSET_VIEW_BANDS.ADDITIONAL),
    field("gas_heating", "Gas Heating", ASSET_VIEW_BANDS.ADDITIONAL),
    field("gas_output_btu", "Gas Output BTU", ASSET_VIEW_BANDS.ADDITIONAL),
    field("electric_heating", "Electric Heating", ASSET_VIEW_BANDS.ADDITIONAL),
    field("electric_heating_kw", "Electric Heating KW", ASSET_VIEW_BANDS.ADDITIONAL),
    field("pump_backup_heat_kw", "Heat Pump - Backup Heat KW", ASSET_VIEW_BANDS.ADDITIONAL),
    field("cooling_only", "Cooling Only", ASSET_VIEW_BANDS.ADDITIONAL),
    field("controls", "Controls", ASSET_VIEW_BANDS.ADDITIONAL),
    field("control_type", "Controls Type", ASSET_VIEW_BANDS.ADDITIONAL),
    field("smoke_detector", "Smoke Detector", ASSET_VIEW_BANDS.ADDITIONAL),
    field("smoke_detector_mounting", "Smoke Detector Mounting", ASSET_VIEW_BANDS.ADDITIONAL),
    field("smoke_detector_supply", "Smoke Detector - Supply", ASSET_VIEW_BANDS.ADDITIONAL),
    field("smoke_detector_return", "Smoke Detector - Return", ASSET_VIEW_BANDS.ADDITIONAL),
    field("curb", "Curb", ASSET_VIEW_BANDS.ADDITIONAL),
    field("curb_description", "Curb Other Description", ASSET_VIEW_BANDS.ADDITIONAL),
    field("vibration_isolation", "Vibration Isolation", ASSET_VIEW_BANDS.ADDITIONAL),
    field("burglar_bars", "Burglar Bars", ASSET_VIEW_BANDS.ADDITIONAL),
    field("installed_on_adapter_curb", "Existing Unit Installed on an Adapter Curb?", ASSET_VIEW_BANDS.ADDITIONAL),
    field("curb_outside_dimension_length", "Existing Curb Outside Dimensions - Length", ASSET_VIEW_BANDS.ADDITIONAL),
    field("curb_outside_dimension_width", "Existing Curb Outside Dimensions - Width", ASSET_VIEW_BANDS.ADDITIONAL),
    field("allow_curb_adaptor", "Will Mall, Landlord, Municipality Allow a Curb Adapter?", ASSET_VIEW_BANDS.ADDITIONAL),
    field("ducted_or_concentric_system", "Is This a Ducted or Concentric System", ASSET_VIEW_BANDS.ADDITIONAL),
    field("unit_weight", "Unit Weight", ASSET_VIEW_BANDS.ADDITIONAL),
    field("codes_for_disconnects", "Any Special Codes for Disconnects", ASSET_VIEW_BANDS.ADDITIONAL),
    field("codes_for_convenience_outlets", "Any Special Codes for Convenience Outlets", ASSET_VIEW_BANDS.ADDITIONAL),
    field("codes_for_roof_lighting", "Any Special Codes for Roof Lighting", ASSET_VIEW_BANDS.ADDITIONAL),
    field("duct_work_attached_to_bottom_of_unit", "Current Duct Work Attached to Bottom of Unit", ASSET_VIEW_BANDS.ADDITIONAL),
    field("aluminium_wire_used", "Was Aluminum Wire Used", ASSET_VIEW_BANDS.ADDITIONAL),
    field("code_or_safety_concerns", "Are There Any Code or Safety Concerns", ASSET_VIEW_BANDS.ADDITIONAL),
    field("distance_from_salt_water_body", "Distance (in Miles) from Body of Salt Water", ASSET_VIEW_BANDS.ADDITIONAL),
    field("recommend_coil_coating", "Recommend Coil Coating", ASSET_VIEW_BANDS.ADDITIONAL),
    field("need_resealed", "Do Electrical/Piping Roof Penetrations Need to be Resealed", ASSET_VIEW_BANDS.ADDITIONAL),
    field("split_system_line_sets_good_condition", "Split System - Are Line Sets in Good Condition", ASSET_VIEW_BANDS.ADDITIONAL),
    field("split_system_line_sets_damaged", "Split System - Are Line Sets Damaged in Any Way", ASSET_VIEW_BANDS.ADDITIONAL),
    field("split_system_line_sets_crimped", "Split System - Are Line Sets Crimped", ASSET_VIEW_BANDS.ADDITIONAL),
    field("split_system_line_sets_insulated", "Split System - Are Line Sets Insulated", ASSET_VIEW_BANDS.ADDITIONAL),
    field("split_system_line_sets_re_used", "Split System - Can Line Sets Be Re-Used", ASSET_VIEW_BANDS.ADDITIONAL),
    field("line_connection_type", "Line Connection Type", ASSET_VIEW_BANDS.ADDITIONAL),

    // Boiler Fields
    field("boiler_type", "Boiler Type", ASSET_VIEW_BANDS.BOILER),
    field("boiler_burner_type", "Boiler Burner Type", ASSET_VIEW_BANDS.BOILER),
    field("boiler_btu_input", "Boiler BTU Input", ASSET_VIEW_BANDS.BOILER),
    field("boiler_btu_output", "Boiler BTU Output", ASSET_VIEW_BANDS.BOILER),
    field("boiler_hp", "Boiler HP", ASSET_VIEW_BANDS.BOILER),
    field("boiler_fuel", "Boiler Fuel", ASSET_VIEW_BANDS.BOILER),
    field("boiler_gas_pressure", "Boiler Gas Pressure", ASSET_VIEW_BANDS.BOILER),
    field("boiler_kw_input", "Boiler KW Input", ASSET_VIEW_BANDS.BOILER),
    field("boiler_power_burner_type", "Boiler Power Burner Type", ASSET_VIEW_BANDS.BOILER),
    field("boiler_burner_model", "Boiler Burner Model", ASSET_VIEW_BANDS.BOILER),
    field("boiler_burner_serial", "Boiler Burner Serial", ASSET_VIEW_BANDS.BOILER),
    field("boiler_pressure_type", "Boiler Operating Pressure Type", ASSET_VIEW_BANDS.BOILER),
    field("confirm_boiler_pressure", "Confirm Boiler Pressure", ASSET_VIEW_BANDS.BOILER),

    // Auxiliary Equipment Fields
    field("auxiliary_equipment_name", "Equipment Name", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_model", "Model", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_serial", "Serial", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_date_manufactured", "Date Manufactured", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_manufacturer", "Manufacturer", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_condition", "Condition", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_eul", "EUL", ASSET_VIEW_BANDS.AUXILIARY),
    field("auxiliary_crv", "CRV", ASSET_VIEW_BANDS.AUXILIARY)
];

export const ASSET_VIEW_FORM_FIELD_ORDER = STATIC_FIELDS.map(f => f.key);

export const dynamicFieldConfigKey = fieldName => `df__${fieldName}`;

const isHiddenVisibility = value =>
    value === false || value === 0 || value === "0" || value === "false";

const resolveDynamicFieldVisibility = (current, saved, def) => {
    if (isHiddenVisibility(current) || isHiddenVisibility(saved)) return false;
    return current ?? saved ?? def?.isVisible;
};

export const buildAssetViewFieldDefaults = () => {
    const config = {};
    const keys = [];
    STATIC_FIELDS.forEach(({ key, label, band, bandForm, isVisible }) => {
        keys.push(key);
        config[key] = { label, band, bandForm, isVisible };
    });
    return { keys, config };
};

export const assetViewFieldDefaults = buildAssetViewFieldDefaults();

export const getAssetViewFieldBand = (key, fieldConfig, layoutContext = "view") => {
    const entry = fieldConfig?.config?.[key];
    if (!entry) return null;
    if (layoutContext === "form") {
        return entry.bandForm ?? entry.band;
    }
    return entry.band;
};

export const buildDynamicFieldEntries = (dynamicBands = {}) => {
    const entries = [];
    if (!dynamicBands || typeof dynamicBands !== "object") return entries;
    Object.entries(dynamicBands).forEach(([bandName, fields]) => {
        if (!Array.isArray(fields)) return;
        fields.forEach(fieldDef => {
            const fieldName = fieldDef?.field_name;
            if (!fieldName) return;
            const key = dynamicFieldConfigKey(fieldName);
            const bandKey = dynamicBandConfigKey(bandName);
            entries.push({
                key,
                label: fieldDef.label || fieldName,
                band: bandKey,
                bandForm: bandKey,
                bandDisplay: bandName,
                isVisible: fieldDef?.rules?.table_default_visible !== false
            });
        });
    });
    return entries;
};

export const mergeDynamicFieldsIntoDefaults = (defaults, dynamicBands = {}) => {
    const dynamicEntries = buildDynamicFieldEntries(dynamicBands);
    if (!dynamicEntries.length) return defaults;

    const keys = [...defaults.keys];
    const config = { ...defaults.config };
    const insertAt = keys.length;

    let offset = 0;
    dynamicEntries.forEach(entry => {
        if (config[entry.key]) {
            config[entry.key] = {
                ...config[entry.key],
                label: entry.label,
                band: entry.band,
                bandForm: entry.bandForm ?? entry.band,
                bandDisplay: entry.bandDisplay,
                isDynamicField: true,
                isVisible: config[entry.key].isVisible ?? entry.isVisible
            };
            if (!keys.includes(entry.key)) {
                keys.splice(insertAt + offset, 0, entry.key);
                offset += 1;
            }
            return;
        }
        keys.splice(insertAt + offset, 0, entry.key);
        offset += 1;
        config[entry.key] = {
            label: entry.label,
            band: entry.band,
            bandForm: entry.bandForm ?? entry.band,
            bandDisplay: entry.bandDisplay,
            isVisible: entry.isVisible,
            isDynamicField: true
        };
    });

    return { keys, config };
};

export const alignAssetViewFieldConfigOrder = (fieldConfig, dynamicBands = {}, layoutContext = "view") => {
    if (!fieldConfig?.config) return fieldConfig;
    const defaults = mergeDynamicFieldsIntoDefaults(buildAssetViewFieldDefaults(), dynamicBands);
    const orderTemplate = defaults.keys;
    const orderSet = new Set(orderTemplate);
    const defaultKeySet = new Set(defaults.keys);
    const rawPresentKeys = fieldConfig.keys || Object.keys(fieldConfig.config);
    const presentKeys = rawPresentKeys.filter(key => defaultKeySet.has(key) || !String(key).startsWith("df__"));
    const presentSet = new Set(presentKeys);

    const ordered = orderTemplate.filter(key => presentSet.has(key));
    presentKeys.forEach(key => {
        if (!orderSet.has(key) && !ordered.includes(key)) {
            ordered.push(key);
        }
    });

    const savedConfig = fieldConfig.config;
    const config = Object.entries(savedConfig).reduce((acc, [key, value]) => {
        const isDynamic = String(key).startsWith("df__");
        if (!isDynamic || defaultKeySet.has(key) || isHiddenVisibility(value?.isVisible)) {
            acc[key] = value;
        }
        return acc;
    }, {});
    const appendKey = key => {
        if (!ordered.includes(key)) ordered.push(key);
    };

    defaults.keys.forEach(key => {
        const def = defaults.config[key];
        if (!def) return;
        if (def.isDynamicField || String(key).startsWith("df__")) {
            const saved = savedConfig[key];
            if (!config[key]) {
                config[key] = saved
                    ? {
                          ...def,
                          ...saved,
                          label: def.label,
                          band: def.band,
                          bandForm: def.bandForm,
                          bandDisplay: def.bandDisplay,
                          isDynamicField: true,
                          isVisible: resolveDynamicFieldVisibility(undefined, saved?.isVisible, def.isVisible)
                      }
                    : { ...def };
            } else {
                config[key] = {
                    ...config[key],
                    label: def.label,
                    band: def.band,
                    bandForm: def.bandForm,
                    bandDisplay: def.bandDisplay,
                    isDynamicField: true,
                    isVisible: resolveDynamicFieldVisibility(config[key].isVisible, saved?.isVisible, def.isVisible)
                };
            }
            appendKey(key);
            return;
        }
        if (config[key] && def) {
            config[key] = {
                ...config[key],
                label: def.label,
                band: def.band,
                bandForm: def.bandForm
            };
        }
    });

    return { keys: ordered, config };
};
